import { createFileRoute, Link } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";

import { pageSchemas } from "@/lib/seo/page-schema";
export const Route = createFileRoute("/bg/privacy-policy")({
  head: () => ({
    meta: [
      { title: "Политика за поверителност | Perceptica" },
      { name: "description", content: "Как Perceptica обработва личните данни в платформата за медиен мониторинг и услугите за медийно разузнаване. Прочетете пълните ангажименти." },
      { property: "og:title", content: "Политика за поверителност | Perceptica" },
      { property: "og:description", content: "Как Perceptica обработва личните данни в платформата за медиен мониторинг и услугите за медийно разузнаване. Прочетете пълните ангажименти." },

      ...ogImageMeta("bg", "/privacy-policy", "Политика за поверителност на Perceptica"),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Политика за поверителност | Perceptica" },
      { name: "twitter:description", content: "Как Perceptica обработва личните данни в платформата за медиен мониторинг и услугите за медийно разузнаване. Прочетете пълните ангажименти." },
      { property: "og:url", content: "https://perceptica.com/bg/privacy-policy" },
      { property: "og:type", content: "website" },
    ],
  
    scripts: pageSchemas({ locale: "bg", path: "/privacy-policy" }),
  }),
  component: PrivacyPageBG,
});

function PrivacyPageBG() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="mx-auto max-w-[760px] px-6 py-20 lg:px-10 lg:py-28">
        <h1 className="font-display text-[clamp(2rem,3.4vw,2.8rem)] font-medium text-foreground">Политика за поверителност</h1>
        <p className="mt-4 text-[14px] text-foreground/70">Последна актуализация: август 2026</p>

        <div className="prose prose-neutral mt-8 max-w-none text-[14.5px] leading-relaxed text-foreground/85">
          <p>
            Perceptica ("Perceptica", "ние") предоставя услуги за медиен мониторинг и анализ на
            екипи по комуникации, PR и маркетинг в цяла Европа. Тази политика за поверителност
            обяснява какви лични данни събираме, когато посещавате нашия уебсайт или използвате
            услугите ни, защо ги обработваме, с кого ги споделяме и какви права имате съгласно
            Общия регламент за защита на данните (GDPR) и българския Закон за защита на личните
            данни.
          </p>

          <h2 className="font-display mt-10 text-xl">1. Администратор на данни</h2>
          <p>
            Perceptica е администратор на личните данни, обработвани чрез този уебсайт и чрез
            платформата за мониторинг, когато сте наш клиент или оценявате услугата. Можете да се
            свържете с нашия екип по всеки въпрос, свързан с поверителността, на{" "}
            <a href="mailto:privacy@perceptica.com" className="underline underline-offset-2">privacy@perceptica.com</a>.
          </p>

          <h2 className="font-display mt-10 text-xl">2. Какви лични данни събираме</h2>
          <ul className="mt-3 space-y-2">
            <li><strong>Данни за контакт и заявки</strong> - информацията, която изпращате чрез
              формите за демо, безплатен пробен период, контакт и кариери (име, служебен имейл,
              компания, роля, държава, съобщението до нас).</li>
            <li><strong>Данни за акаунт</strong> - ако станете клиент, идентификационните данни,
              профилните и конфигурационните данни, необходими за работата на вашия workspace в
              платформата.</li>
            <li><strong>Данни за използване</strong> - продуктова аналитика за това как се
              използват платформата и уебсайтът (разгледани страници, отворени функции, грешки).
              На публичния уебсайт поставяме незадължителни аналитични бисквитки само след вашето
              съгласие чрез бисквитковия банер.</li>
            <li><strong>Технически данни</strong> - сървърни логове и логове за сигурност (IP
              адрес, user agent, времеви маркери), необходими за функционирането, защитата и
              поддръжката на услугата.</li>
            <li><strong>Бисквитки</strong> - вижте нашата{" "}
              <Link to="/cookie-policy" className="underline underline-offset-2">Политика за бисквитки</Link>{" "}
              за категориите, които използваме, и как можете да промените избора си по всяко време.</li>
          </ul>

          <h2 className="font-display mt-10 text-xl">3. Защо използваме данните и правно основание</h2>
          <ul className="mt-3 space-y-2">
            <li><strong>За да отговорим на вас</strong> (заявки за демо, пробен период, контакт,
              кариери) - въз основа на стъпки, предприети по ваше искане преди сключване на
              договор (чл. 6, ал. 1, б. "б" GDPR), и нашия легитимен интерес да отговаряме на
              бизнес запитвания (чл. 6, ал. 1, б. "е" GDPR).</li>
            <li><strong>За да предоставяме услугата Perceptica</strong> на нашите клиенти -
              изпълнение на договор с клиента (чл. 6, ал. 1, б. "б" GDPR).</li>
            <li><strong>За да поддържаме услугата сигурна и надеждна</strong> - нашият легитимен
              интерес да защитаваме инфраструктурата, клиентите и потребителите си (чл. 6, ал. 1,
              б. "е" GDPR).</li>
            <li><strong>За да измерваме и подобряваме уебсайта</strong> - вашето съгласие за
              аналитични и маркетингови бисквитки (чл. 6, ал. 1, б. "а" GDPR).</li>
            <li><strong>За изпълнение на законови задължения</strong> като счетоводство, данъчно
              отчитане и отговор на валидни искания от органи (чл. 6, ал. 1, б. "в" GDPR).</li>
          </ul>

          <h2 className="font-display mt-10 text-xl">4. С кого споделяме данни</h2>
          <p>
            Не продаваме лични данни. Споделяме ги само с проверени доставчици на услуги, които
            действат като наши обработващи лица по писмени споразумения за обработване на данни -
            например хостинг в облак, доставка на имейли, поддръжка на клиенти, продуктова
            аналитика и CRM инструменти. Когато това се изисква, можем да разкриваме данни и пред
            регулатори, съдилища или правоохранителни органи при наличие на валидно правно
            основание.
          </p>

          <h2 className="font-display mt-10 text-xl">5. Международни трансфери</h2>
          <p>
            Perceptica е изградена около резиденция на данните в ЕС. Данните на клиентите се
            съхраняват по подразбиране в Европейския съюз. Когато подизпълнител обработва данни
            извън ЕС/ЕИП, разчитаме на Стандартните договорни клаузи на Европейската комисия и на
            допълнителни мерки, изисквани съгласно Глава V от GDPR.
          </p>

          <h2 className="font-display mt-10 text-xl">6. Колко дълго съхраняваме данните</h2>
          <p>
            Съхраняваме лични данни само толкова, колкото е необходимо за целта, за която са
            събрани. На практика:
          </p>
          <ul className="mt-3 space-y-2">
            <li>Заявки за демо, пробен период и контакт - до 24 месеца от последното
              взаимодействие, след което се изтриват или анонимизират.</li>
            <li>Данни за клиентски акаунти - за срока на договора и кратък период след
              прекратяването му за резервни копия и решаване на спорове.</li>
            <li>Сървърни логове и логове за сигурност - обикновено до 12 месеца.</li>
            <li>Записи, които сме длъжни да пазим по закон (например фактури) - за срока,
              определен от съответния закон.</li>
          </ul>

          <h2 className="font-display mt-10 text-xl">7. Вашите права</h2>
          <p>Имате право да:</p>
          <ul className="mt-3 space-y-2">
            <li>получите достъп до личните данни, които съхраняваме за вас;</li>
            <li>поискате коригиране или допълване на неточни данни;</li>
            <li>поискате изтриване на данните си ("право да бъдеш забравен"), когато са налице
              условията;</li>
            <li>ограничите или възразите срещу определено обработване, включително директен
              маркетинг;</li>
            <li>получите данните си в преносим формат;</li>
            <li>оттеглите съгласие, което сте дали по-рано, без това да засяга предишното
              обработване;</li>
            <li>подадете жалба до надзорен орган - в България това е Комисията за защита на
              личните данни (КЗЛД, <a href="https://www.cpdp.bg" className="underline underline-offset-2" target="_blank" rel="noreferrer">cpdp.bg</a>),
              или до органа в държавата ви на пребиваване или работа.</li>
          </ul>
          <p className="mt-4">
            За да упражните някое от тези права, пишете на{" "}
            <a href="mailto:privacy@perceptica.com" className="underline underline-offset-2">privacy@perceptica.com</a>.
            Ще ви отговорим в сроковете, определени от GDPR.
          </p>

          <h2 className="font-display mt-10 text-xl">8. Сигурност</h2>
          <p>
            Защитаваме личните данни с подходящи технически и организационни мерки, включително
            криптиране при пренос, контрол на достъпа на база роли, журнал за одит, редовни
            резервни копия и прегледи на сигурността на нашата инфраструктура и подизпълнители.
          </p>

          <h2 className="font-display mt-10 text-xl">9. Бисквитки</h2>
          <p>
            Нашият уебсайт използва строго необходими бисквитки и, с ваше съгласие, аналитични и
            маркетингови бисквитки. Можете да прегледате категориите и да промените избора си по
            всяко време от страницата{" "}
            <Link to="/cookie-policy" className="underline underline-offset-2">Политика за бисквитки</Link>.
          </p>

          <h2 className="font-display mt-10 text-xl">10. Деца</h2>
          <p>
            Perceptica е B2B услуга. Тя не е насочена към деца под 16 години и ние не събираме
            съзнателно техни лични данни. Ако смятате, че дете е споделило данни с нас, моля
            свържете се с нас, за да ги изтрием.
          </p>

          <h2 className="font-display mt-10 text-xl">11. Промени в тази политика</h2>
          <p>
            Можем да актуализираме тази Политика за поверителност с развитието на услугата ни или
            на законодателството. Текущата версия винаги се публикува на тази страница с нова дата
            "Последна актуализация". Съществените промени ще бъдат съобщавани чрез уебсайта или,
            когато е уместно, директно на нашите клиенти.
          </p>

          <h2 className="font-display mt-10 text-xl">12. Контакт</h2>
          <p>
            При въпрос относно тази политика или начина, по който Perceptica обработва лични
            данни, свържете се с нас на{" "}
            <a href="mailto:privacy@perceptica.com" className="underline underline-offset-2">privacy@perceptica.com</a>.
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
